import React, { useCallback, useEffect, useState } from "react";
import { Sparkles, Send, RefreshCw, Trash2, Wand2, ChevronDown, ChevronUp, Plus, Image as ImageIcon } from "lucide-react";
import { crmApi } from "../../lib/crmApi";

const inputCls = "w-full border border-[#6E1F2C]/25 bg-white px-2.5 py-1.5 text-[12px] text-[#1C110C] focus:outline-none focus:border-[#6E1F2C]";
const S_COLORS = { prompt_suggested: "#2C5F6E", queued: "#A6822F", generating: "#A6822F", awaiting_approval: "#2C5F6E", sent: "#3F6E2C", saved: "#3F6E2C", failed: "#A3141A", send_failed: "#A3141A", rejected: "#7A6B5F" };
const DISCLAIMER = "Preliminary visual concept — subject to site measurement, technical review, final material selection and approval.";
const LIVE = ["prompt_suggested", "queued", "generating", "awaiting_approval", "send_failed"];

function MediaImg({ id, selected, onClick, h = "h-32" }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let live = true;
    crmApi.fetchMediaUrl(id).then((u) => live && setUrl(u)).catch(() => {});
    return () => { live = false; };
  }, [id]);
  if (!url) return <div className={`${h} w-full bg-[#6E1F2C]/8 flex items-center justify-center text-[#6E1F2C]`}><ImageIcon size={18} /></div>;
  return (
    <img src={url} alt="concept" onClick={onClick}
      className={`${h} w-full object-cover cursor-pointer ${selected ? "ring-2 ring-[#3F6E2C]" : onClick ? "opacity-80 hover:opacity-100" : ""}`} />
  );
}

export function ConceptTaskCard({ task, meta = {}, onChanged }) {
  const [form, setForm] = useState({});
  const [caption, setCaption] = useState("");
  const [sel, setSel] = useState(0);
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");

  useEffect(() => {
    setForm({
      final_prompt: task.final_prompt || task.suggested_prompt || "",
      visual_type: task.visual_type || "photorealistic",
      style_preset: task.style_preset || "",
      num_images: task.num_images || 1,
    });
    setCaption(task.caption || "");
    setSel(task.selected_image_index || 0);
  }, [task.id, task.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (key, fn, ok) => {
    setBusy(key); setMsg("");
    try { const r = await fn(); setMsg(typeof ok === "function" ? ok(r) : ok || "Done ✓"); onChanged?.(); }
    catch (e) { setMsg(e.message); }
    finally { setBusy(""); }
  };
  const savePrompt = () => act("save", () => crmApi.visualUpdate(task.id, {
    final_prompt: form.final_prompt, visual_type: form.visual_type,
    style_preset: form.style_preset || "", num_images: Number(form.num_images),
  }), "Prompt saved ✓");
  const regen = () => act("regen", async () => {
    const r = await crmApi.visualSuggestPrompt(task.id);
    setForm((f) => ({ ...f, final_prompt: r.suggested_prompt }));
  }, "New AI suggestion loaded — review before generating");
  const generate = () => act("gen", async () => {
    await crmApi.visualUpdate(task.id, {
      final_prompt: form.final_prompt, visual_type: form.visual_type,
      style_preset: form.style_preset || "", num_images: Number(form.num_images),
    });
    return crmApi.visualGenerate(task.id, {});
  }, "Generation queued — images appear here when ready");
  const send = () => act("send", () => crmApi.visualSend(task.id, { image_index: sel, caption }), "Sent to customer ✓");
  const saveOnly = () => act("keep", () => crmApi.visualSave(task.id), "Saved to library without sending ✓");
  const reject = () => act("reject", () => crmApi.visualReject(task.id), "Rejected — library copies deactivated");
  const del = () => window.confirm("Delete this concept task?") && act("del", () => crmApi.visualDelete(task.id), "Deleted");
  const retry = () => act("retry", () => crmApi.visualRetry(task.id), "Retrying…");

  const st = task.status;
  const images = task.images || [];
  const editing = st === "prompt_suggested" || st === "failed";
  const reviewable = st === "awaiting_approval" || st === "saved" || st === "send_failed";

  return (
    <div className="border border-[#6E1F2C]/15 bg-white p-3 space-y-2" data-testid={`concept-card-${task.id}`}>
      <div className="flex justify-between gap-2 items-center">
        <p className="text-[11px] font-medium text-[#1C110C]">{task.lead_ref} <span className="text-[#7A6B5F] font-normal">· {task.requested_by}</span></p>
        <span className="text-[9px] uppercase px-2 py-0.5 border" data-testid="concept-status"
          style={{ color: S_COLORS[st], borderColor: `${S_COLORS[st]}55` }}>{(st || "").replace(/_/g, " ")}</span>
      </div>
      {task.original_request && (
        <p className="text-[10px] text-[#7A6B5F]"><span className="uppercase tracking-[0.1em] text-[#A6822F]">Customer asked:</span> {task.original_request}</p>
      )}

      {editing && (
        <div className="space-y-2" data-testid="concept-prompt-review">
          <label className="block text-[9px] tracking-[0.18em] uppercase text-[#7A6B5F]">AI-suggested prompt — review &amp; edit before generating</label>
          <textarea rows={4} className={inputCls} data-testid="concept-prompt-input"
            value={form.final_prompt || ""} onChange={(e) => setForm({ ...form, final_prompt: e.target.value })} />
          <div className="grid grid-cols-3 gap-2">
            <select className={inputCls} data-testid="concept-type-select" value={form.visual_type}
              onChange={(e) => setForm({ ...form, visual_type: e.target.value })}>
              {(meta.visual_types || ["photorealistic", "mood_board", "sketch", "material_palette"]).map((t) => (
                <option key={t} value={t}>{t.replace(/_/g, " ")}</option>
              ))}
            </select>
            <select className={inputCls} data-testid="concept-style-select" value={form.style_preset}
              onChange={(e) => setForm({ ...form, style_preset: e.target.value })}>
              <option value="">style (optional)</option>
              {(meta.visual_styles || ["contemporary", "modern", "classic", "luxury", "minimal"]).map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <select className={inputCls} data-testid="concept-count-select" value={form.num_images}
              onChange={(e) => setForm({ ...form, num_images: e.target.value })}>
              {Array.from({ length: meta.max_images || 3 }, (_, i) => i + 1).map((n) => (
                <option key={n} value={n}>{n} image{n > 1 ? "s" : ""}</option>
              ))}
            </select>
          </div>
          {(task.reference_file_ids || []).length > 0 && (
            <div>
              <p className="text-[9px] text-[#7A6B5F] uppercase tracking-[0.12em] mb-1">Customer room photos used as reference ({task.reference_file_ids.length})</p>
              <div className="flex gap-1.5">
                {task.reference_file_ids.slice(0, 4).map((fid) => <div key={fid} className="w-14"><MediaImg id={fid} h="h-14" /></div>)}
              </div>
            </div>
          )}
          <div className="flex gap-1.5 flex-wrap">
            <button type="button" data-testid="concept-regen-btn" onClick={regen} disabled={!!busy}
              className="border border-[#A6822F]/40 text-[#A6822F] px-2.5 py-1 text-[9px] uppercase tracking-[0.12em] inline-flex items-center gap-1 disabled:opacity-40">
              <Wand2 size={10} /> {busy === "regen" ? "Thinking…" : "Regenerate suggestion"}
            </button>
            <button type="button" data-testid="concept-saveprompt-btn" onClick={savePrompt} disabled={!!busy}
              className="border border-[#6E1F2C]/40 text-[#6E1F2C] px-2.5 py-1 text-[9px] uppercase tracking-[0.12em] disabled:opacity-40">
              Save prompt
            </button>
            {st === "failed"
              ? <button type="button" data-testid="concept-retry-btn" onClick={retry} disabled={!!busy}
                  className="bg-[#6E1F2C] text-white px-2.5 py-1 text-[9px] uppercase tracking-[0.12em] inline-flex items-center gap-1 disabled:opacity-40">
                  <RefreshCw size={10} /> Retry generation
                </button>
              : <button type="button" data-testid="concept-generate-btn" onClick={generate} disabled={!!busy || !(form.final_prompt || "").trim()}
                  className="bg-[#6E1F2C] text-white px-2.5 py-1 text-[9px] uppercase tracking-[0.12em] inline-flex items-center gap-1 disabled:opacity-40">
                  <Sparkles size={10} /> {busy === "gen" ? "Queueing…" : "Generate"}
                </button>}
            <button type="button" data-testid="concept-reject-btn" onClick={reject} disabled={!!busy}
              className="border border-[#A3141A]/40 text-[#A3141A] px-2.5 py-1 text-[9px] uppercase tracking-[0.12em] disabled:opacity-40">Reject</button>
          </div>
          {st === "failed" && task.error && <p className="text-[10px] text-[#A3141A]">{task.error.slice(0, 160)}</p>}
        </div>
      )}

      {(st === "queued" || st === "generating") && (
        <p className="text-[11px] text-[#A6822F] animate-pulse" data-testid="concept-generating">
          Generating {task.num_images || 1} image{(task.num_images || 1) > 1 ? "s" : ""} in the background… ordinary WhatsApp replies are not affected.
        </p>
      )}

      {reviewable && images.length > 0 && (
        <div className="space-y-2" data-testid="concept-approval">
          <div className={`grid gap-1.5 ${images.length > 1 ? "grid-cols-2" : "grid-cols-1"}`}>
            {images.map((im, i) => (
              <div key={im.file_media_id} data-testid={`concept-image-${i}`}>
                <MediaImg id={im.file_media_id} selected={sel === i} onClick={() => setSel(i)} />
                {im.quality && (
                  <p className={`text-[9px] mt-0.5 ${im.quality.passed ? "text-[#3F6E2C]" : "text-[#A3141A]"}`} data-testid={`concept-quality-${i}`}>
                    {im.quality.passed ? "✓" : "⚠"} Photorealism {im.quality.photorealism_score} · Brief match {im.quality.brief_match_score}
                  </p>
                )}
              </div>
            ))}
          </div>
          {images[sel]?.quality?.concerns?.length > 0 && (
            <p className="text-[9px] text-[#A6822F]" data-testid="concept-quality-concerns">
              QC concerns (internal): {images[sel].quality.concerns.join("; ")}
            </p>
          )}
          {task.regen_count > 0 && (
            <p className="text-[9px] text-[#7A6B5F]">Auto-regenerated {task.regen_count}× to meet the quality standard.</p>
          )}
          {images.length > 1 && <p className="text-[9px] text-[#7A6B5F]">Click an image to select it for sending (image {sel + 1} selected).</p>}
          <label className="block text-[9px] tracking-[0.18em] uppercase text-[#7A6B5F]">Caption — disclaimer is always appended</label>
          <textarea rows={2} className={inputCls} data-testid="concept-caption-input" value={caption}
            onChange={(e) => setCaption(e.target.value)} placeholder="Optional message above the disclaimer…" />
          <p className="text-[9px] text-[#7A6B5F] italic">{DISCLAIMER}</p>
          <div className="flex gap-1.5 flex-wrap">
            <button type="button" data-testid="concept-send-btn" onClick={send} disabled={!!busy}
              className="bg-[#3F6E2C] text-white px-2.5 py-1 text-[9px] uppercase tracking-[0.12em] inline-flex items-center gap-1 disabled:opacity-40">
              <Send size={10} /> {busy === "send" ? "Sending…" : st === "send_failed" ? "Retry send" : "Approve & send"}
            </button>
            {st === "awaiting_approval" && (
              <button type="button" data-testid="concept-savenosend-btn" onClick={saveOnly} disabled={!!busy}
                className="border border-[#6E1F2C]/40 text-[#6E1F2C] px-2.5 py-1 text-[9px] uppercase tracking-[0.12em] disabled:opacity-40">Save without sending</button>
            )}
            <button type="button" data-testid="concept-reject-btn" onClick={reject} disabled={!!busy}
              className="border border-[#A3141A]/40 text-[#A3141A] px-2.5 py-1 text-[9px] uppercase tracking-[0.12em] disabled:opacity-40">Reject</button>
          </div>
          {st === "send_failed" && task.error && <p className="text-[10px] text-[#A3141A]">Send failed: {task.error.slice(0, 140)}</p>}
        </div>
      )}

      {reviewable && images.length === 0 && task.image_base64 && (
        <div className="space-y-1.5">
          <img alt="concept" src={`data:image/png;base64,${task.image_base64}`} className="w-full h-32 object-cover" />
          <p className="text-[9px] text-[#7A6B5F] italic">Legacy task from the previous workflow — generate a new concept to use the send controls.</p>
        </div>
      )}

      {st === "sent" && (
        <div className="space-y-1.5">
          {images.length > 0 && <MediaImg id={images[task.selected_image_index || 0]?.file_media_id} />}
          {!images.length && task.image_base64 && <img alt="concept" src={`data:image/png;base64,${task.image_base64}`} className="w-full h-32 object-cover" />}
          <p className="text-[10px] text-[#3F6E2C]">Sent {task.sent_at?.slice(0, 16).replace("T", " ")} · approved by {task.approved_by}</p>
          {task.caption && <p className="text-[10px] text-[#7A6B5F] whitespace-pre-wrap">{task.caption}</p>}
        </div>
      )}

      {st === "rejected" && (
        <button type="button" data-testid="concept-delete-btn" onClick={del} disabled={!!busy}
          className="border border-[#7A6B5F]/40 text-[#7A6B5F] px-2.5 py-1 text-[9px] uppercase tracking-[0.12em] inline-flex items-center gap-1"><Trash2 size={10} /> Delete</button>
      )}

      <div className="flex justify-between items-center text-[9px] text-[#7A6B5F]">
        <span>{task.created_at?.slice(0, 16).replace("T", " ")}{task.generated_by ? ` · generated by ${task.generated_by}` : ""}</span>
        {task.cost_usd > 0 && <span data-testid="concept-cost">~${Number(task.cost_usd).toFixed(2)}</span>}
      </div>
      {msg && <p className="text-[10px] text-[#A6822F]" data-testid="concept-msg">{msg}</p>}
    </div>
  );
}

export function ConceptPanel({ leadId, onChanged }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const load = useCallback(() => crmApi.visualsList(leadId).then(setData).catch(() => {}), [leadId]);
  useEffect(() => { setOpen(false); load(); const t = setInterval(load, 12000); return () => clearInterval(t); }, [load]);

  const tasks = (data?.tasks || []).filter((t) => LIVE.includes(t.status));
  const create = async () => {
    setMsg("");
    try {
      const r = await crmApi.visualCreate({ lead_id: leadId, prompt_context: "" });
      if (!r.ok) setMsg(r.detail || "Could not create");
      else setOpen(true);
      load();
    } catch (e) { setMsg(e.message); }
  };
  if (!data) return null;

  return (
    <div className="border-t border-[#6E1F2C]/10 bg-[#FAF5EC]" data-testid="concept-panel">
      <div className="flex items-center justify-between px-4 py-1.5">
        <button type="button" data-testid="concept-panel-toggle" onClick={() => setOpen((o) => !o)}
          className="text-[9px] tracking-[0.16em] uppercase text-[#6E1F2C] inline-flex items-center gap-1.5">
          <Sparkles size={11} /> Concept studio {tasks.length > 0 && <span className="bg-[#6E1F2C] text-white px-1.5">{tasks.length}</span>}
          {open ? <ChevronDown size={11} /> : <ChevronUp size={11} />}
        </button>
        <div className="flex items-center gap-2">
          {msg && <span className="text-[9px] text-[#A3141A]">{msg}</span>}
          <button type="button" data-testid="concept-new-btn" onClick={create}
            className="text-[9px] tracking-[0.14em] uppercase border border-[#6E1F2C]/30 text-[#6E1F2C] px-2 py-0.5 inline-flex items-center gap-1 hover:bg-[#6E1F2C]/10">
            <Plus size={10} /> New concept
          </button>
        </div>
      </div>
      {open && tasks.length > 0 && (
        <div className="px-4 pb-3 space-y-2 max-h-[340px] overflow-y-auto">
          {tasks.map((t) => (
            <ConceptTaskCard key={t.id} task={t} onChanged={() => { load(); onChanged?.(); }}
              meta={{ visual_types: data.visual_types, visual_styles: data.visual_styles, max_images: data.max_images }} />
          ))}
        </div>
      )}
      {open && tasks.length === 0 && (
        <p className="px-4 pb-2 text-[10px] text-[#7A6B5F] italic">No open concept tasks — the AI suggests one when the customer asks for a visual, or click "New concept".</p>
      )}
    </div>
  );
}

export function VisualsWorkflowTab() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(() => crmApi.visualsList().then(setData).catch((e) => setErr(e.message)), []);
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [load]);

  const u = data?.usage || {};
  const pct = u.budget_usd ? Math.min(100, Math.round((u.cost_usd / u.budget_usd) * 100)) : 0;

  return (
    <div className="space-y-3">
      <p className="text-[11px] text-[#7A6B5F]">
        Hybrid workflow: the AI suggests a prompt when a customer asks for a visual → staff review, edit and generate → staff approve, caption and send.
        Nothing is sent to a customer without staff approval{data?.auto_send_enabled ? " (⚠ admin auto-send is currently ON)" : ""}. Every generated image is saved to the Media Library as an AI concept.
      </p>
      <div className="border border-[#6E1F2C]/15 bg-white p-3 flex items-center gap-4 flex-wrap" data-testid="concept-usage">
        <div className="min-w-[180px] flex-1">
          <div className="flex justify-between text-[10px] text-[#7A6B5F] mb-1">
            <span>Monthly budget — {u.month}</span>
            <span>${(u.cost_usd || 0).toFixed(2)} / ${(u.budget_usd || 0).toFixed(2)}</span>
          </div>
          <div className="h-1.5 bg-[#6E1F2C]/10 w-full">
            <div className={`h-1.5 ${pct >= 90 ? "bg-[#A3141A]" : "bg-[#3F6E2C]"}`} style={{ width: `${pct}%` }} />
          </div>
        </div>
        <p className="text-[10px] text-[#7A6B5F]">{u.generations || 0} generation{(u.generations || 0) === 1 ? "" : "s"} · {u.images || 0} image{(u.images || 0) === 1 ? "" : "s"} this month</p>
      </div>
      {err && <p className="text-[11px] text-[#A3141A]" data-testid="visuals-msg">{err}</p>}
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="visuals-list">
        {(data?.tasks || []).map((t) => (
          <ConceptTaskCard key={t.id} task={t} onChanged={load}
            meta={{ visual_types: data.visual_types, visual_styles: data.visual_styles, max_images: data.max_images }} />
        ))}
        {data && data.tasks.length === 0 && (
          <p className="text-xs text-[#7A6B5F] italic">No concept tasks yet. They appear when a customer asks for a visual, or create one from a conversation's Concept studio.</p>
        )}
      </div>
    </div>
  );
}
